import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  alpha,
} from "@mui/material";
import { MotionBox } from "@rusha/shared-theme";
import { JsonLd } from "../common/JsonLd";

interface Step {
  num: string;
  iconSrc: string;
  iconAlt: string;
  title: string;
  description: string;
  bullets: string[];
  accent: string;
  code?: { file: string; lines: { text: string; color?: string }[] };
  metrics?: { label: string; value: string; color: string }[];
  hasMiniDashboard?: boolean;
}

const STEPS: Step[] = [
  {
    num: "01",
    iconSrc: "/assets/howitworks/step-icon-build.svg",
    iconAlt: "Build step icon",
    title: "Build",
    description:
      "Provision infrastructure from battle-tested templates. Define everything as code.",
    bullets: [
      "Terraform & Pulumi templates",
      "Multi-cloud environment parity",
      "Policy-as-code guardrails",
    ],
    accent: "#3b82f6",
    code: {
      file: "# rusha.yaml",
      lines: [
        { text: "template: aws-eks-v2", color: "#3b82f6" },
        { text: "region: us-east-1", color: "#10b981" },
        { text: "nodes: 3  # auto-scaled", color: "#10b981" },
      ],
    },
  },
  {
    num: "02",
    iconSrc: "/assets/howitworks/step-icon-deploy.svg",
    iconAlt: "Deploy step icon",
    title: "Deploy",
    description:
      "GitOps-powered delivery with automatic drift detection. Roll forward or rollback.",
    bullets: [
      "ArgoCD & Flux native support",
      "Canary & blue-green deployments",
      "Real-time deployment status",
    ],
    accent: "#7c3aed",
    code: {
      file: "# .rusha/deploy.yml",
      lines: [
        { text: "strategy: canary", color: "#7c3aed" },
        { text: "traffic: 10% → 50% → 100%", color: "#10b981" },
        { text: "auto_rollback: true", color: "#7c3aed" },
      ],
    },
  },
  {
    num: "03",
    iconSrc: "/assets/howitworks/step-icon-operate.svg",
    iconAlt: "Operate step icon",
    title: "Operate",
    description:
      "AI continuously monitors health, cost, and security. Actionable recommendations.",
    bullets: [
      "Unified logs, metrics & traces",
      "AI cost optimization",
      "Automated security posture",
    ],
    accent: "#10b981",
    metrics: [
      { label: "↓ 18% costs", value: "18", color: "#10b981" },
      { label: "↑ 99.9% uptime", value: "99.9", color: "#3b82f6" },
    ],
    hasMiniDashboard: true,
  },
];

const FAQS = [
  {
    q: "How does Rusha build infrastructure?",
    a: "Rusha provisions infrastructure from battle-tested Terraform and Pulumi templates. You define everything as code in a rusha.yaml file, and Rusha handles multi-cloud environment parity with policy-as-code guardrails.",
  },
  {
    q: "How does Rusha deploy applications?",
    a: "Rusha uses GitOps-powered delivery with native ArgoCD and Flux support. Automatic drift detection, canary and blue-green deployments, and real-time deployment status. Roll forward or rollback with a single click.",
  },
  {
    q: "How does Rusha operate running systems?",
    a: "AI continuously monitors health, cost, and security. Unified logs, metrics, and traces. AI cost optimization identifies savings, and automated security posture management keeps workloads safe.",
  },
  {
    q: "Do I need to manage Kubernetes directly?",
    a: "No. Rusha abstracts away the Kubernetes complexity. You define your system in a manifest, push to Git, and Rusha handles building, deploying, and operating — no kubectl or YAML wrestling required.",
  },
];

const HowItWorks: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      component="section"
      id="how-it-works"
      aria-labelledby="hiw-title"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: "background.default",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Section header */}
        <MotionBox
          sx={{ textAlign: "center", mb: { xs: 5, md: 8 } }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            component="h2"
            variant="h2"
            id="hiw-title"
            sx={{
              mb: 1.5,
              fontWeight: 800,
              fontSize: { xs: "1.75rem", md: "2.5rem" },
            }}
          >
            From idea to production in one platform
          </Typography>
          <Typography
            component="p"
            sx={{
              color: "text.secondary",
              fontSize: "1.05rem",
              maxWidth: 600,
              mx: "auto",
            }}
          >
            No more context switching between a dozen tools.
          </Typography>
        </MotionBox>

        {/* Step cards */}
        <Grid container spacing={3} sx={{ position: "relative" }}>
          {STEPS.map((step, i) => (
            <Grid item xs={12} md={4} key={step.num}>
              <MotionBox
                component="article"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                sx={{
                  height: "100%",
                  p: 3,
                  borderRadius: 3,
                  bgcolor: "background.paper",
                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  position: "relative",
                  transition: "border-color 200ms ease, box-shadow 200ms ease",
                  "&:hover": {
                    borderColor: alpha(step.accent, 0.4),
                    boxShadow: `0 12px 32px ${alpha(step.accent, 0.1)}`,
                  },
                }}
              >
                {/* Step badge + icon */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      px: 1.25,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.text.secondary, 0.08),
                      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: "text.secondary",
                      letterSpacing: "0.04em",
                    }}
                  >
                    STEP {step.num}
                  </Box>
                  <img
                    src={step.iconSrc}
                    alt={step.iconAlt}
                    width="40"
                    height="40"
                    loading="lazy"
                  />
                </Box>

                {/* Title */}
                <Typography
                  component="h3"
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    mb: 1,
                    color: "text.primary",
                  }}
                >
                  {step.title}
                </Typography>

                {/* Description */}
                <Typography
                  component="p"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.9rem",
                    lineHeight: 1.6,
                    mb: 2,
                  }}
                >
                  {step.description}
                </Typography>

                {/* Bullets */}
                <Box
                  component="ul"
                  sx={{ listStyle: "none", p: 0, m: 0, mb: 2.5 }}
                >
                  {step.bullets.map((b) => (
                    <Box
                      component="li"
                      key={b}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 0.5,
                        fontSize: "0.85rem",
                        color: "text.primary",
                      }}
                    >
                      <Box
                        sx={{
                          width: 5,
                          height: 5,
                          borderRadius: "50%",
                          bgcolor: step.accent,
                          flexShrink: 0,
                        }}
                      />
                      {b}
                    </Box>
                  ))}
                </Box>

                {/* Code block OR metric chips + mini dashboard */}
                {step.code && (
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.75,
                      borderRadius: 2,
                      bgcolor: isDark ? "#0c0a18" : "#0f172a",
                      border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                      overflow: "auto",
                    }}
                  >
                    <Typography
                      component="code"
                      sx={{
                        display: "block",
                        fontFamily: '"SF Mono", Monaco, monospace',
                        fontSize: "0.75rem",
                        color: "#64748b",
                        mb: 0.75,
                      }}
                    >
                      {step.code.file}
                    </Typography>
                    {step.code.lines.map((line, idx) => (
                      <Typography
                        key={idx}
                        component="code"
                        sx={{
                          display: "block",
                          fontFamily: '"SF Mono", Monaco, monospace',
                          fontSize: "0.8rem",
                          color: "#e2e8f0",
                          lineHeight: 1.6,
                        }}
                      >
                        {line.color ? (
                          <Box component="span" sx={{ color: line.color }}>
                            {line.text.split(":")[0]}:
                          </Box>
                        ) : null}
                        {line.color
                          ? line.text.substring(line.text.indexOf(":") + 1)
                          : line.text}
                      </Typography>
                    ))}
                  </Box>
                )}

                {step.metrics && (
                  <Box
                    sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}
                  >
                    {step.metrics.map((m) => (
                      <Box
                        key={m.label}
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 1.5,
                          bgcolor: alpha(m.color, 0.08),
                          border: `1px solid ${alpha(m.color, 0.25)}`,
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          color: m.color,
                        }}
                      >
                        {m.label}
                      </Box>
                    ))}
                  </Box>
                )}

                {step.hasMiniDashboard && (
                  <Box
                    component="img"
                    src={`/assets/howitworks/mini-dashboard-${isDark ? "dark" : "light"}.svg`}
                    alt=""
                    aria-hidden="true"
                    width="290"
                    height="50"
                    loading="lazy"
                    sx={{ width: "100%", height: "auto", borderRadius: 1.5 }}
                  />
                )}
              </MotionBox>
            </Grid>
          ))}
        </Grid>

        {/* Bottom CTA bar */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          sx={{
            mt: { xs: 4, md: 6 },
            mx: "auto",
            maxWidth: 560,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            px: 3,
            py: 2,
            borderRadius: 2.5,
            bgcolor: "background.paper",
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: "0.95rem", color: "text.primary" }}>
            Ready to unify your engineering workflow?
          </Typography>
          <Typography
            component="a"
            href="https://app.rusha.tierravivaai.net"
            sx={{
              px: 2,
              py: 1,
              borderRadius: 1.5,
              bgcolor: "primary.main",
              color: "#fff",
              fontSize: "0.85rem",
              fontWeight: 600,
              textDecoration: "none",
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            Start free →
          </Typography>
        </MotionBox>
      </Container>

      {/* FAQPage JSON-LD for rich results */}
      <JsonLd
        data={{
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
    </Box>
  );
};

export default HowItWorks;
